import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerContext } from '../../hooks/useCustomerContext';
import { useActiveOrders } from '../../hooks/useActiveOrders';
import { NoSymbolIcon, PhoneInboundIcon, PhoneOutboundIcon } from './icons';

interface Props {
  phone: string;
  compact?: boolean;
}

function statusBadgeClass(status: string): string {
  if (status.includes('ingepland')) return 'bg-emerald-50 text-emerald-700';
  if (status.includes('plannen') || status.includes('voorstel')) return 'bg-amber-50 text-amber-700';
  return 'bg-slate-100 text-slate-600';
}

function relativeTime(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const sec = Math.round(diff / 1000);
  if (sec < 60) return `${sec}s geleden`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min geleden`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `${hrs}u geleden`;
  const days = Math.round(hrs / 24);
  if (days < 31) return `${days}d geleden`;
  return d.toLocaleDateString('nl-NL');
}

function formatEuro(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '';
  return value.toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

export function CustomerContextPanel({ phone, compact = false }: Props) {
  const { customer, recentCalls, openOffertes, isLoading, error } = useCustomerContext(phone);
  const navigate = useNavigate();
  const accountId = customer?.type === 'account' ? customer.id : null;
  const { orders, isLoading: ordersLoading } = useActiveOrders(accountId);
  const [showMore, setShowMore] = useState(false);

  if (isLoading) {
    return (
      <div className="p-3 space-y-2">
        <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
        <div className="h-3 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="h-3 w-40 bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 text-xs text-gray-400">
        Kon context niet laden.
      </div>
    );
  }

  const displayName = customer?.name || 'Onbekend nummer';

  return (
    <div className={compact ? 'p-2 space-y-2' : 'p-3 space-y-3'}>
      {customer?.blacklisted && (
        <div className="rounded-md bg-red-600 text-white px-2.5 py-1.5 text-xs">
          <span className="font-semibold inline-flex items-center gap-1">
            <NoSymbolIcon className="w-3.5 h-3.5 shrink-0" />
            Blacklist — geen zaken meer doen.
          </span>
          {customer.blacklistReden && (
            <span className="block text-red-100 mt-0.5">{customer.blacklistReden}</span>
          )}
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm text-gray-900">{displayName}</div>
          <div className="text-xs text-gray-500">
            {phone}
            {customer?.city ? ` · ${customer.city}` : ''}
            {customer?.type === 'lead' ? ' · Lead' : ''}
          </div>
        </div>
        {customer && (
          <button
            type="button"
            onClick={() =>
              navigate(customer.type === 'account' ? `/klanten/${customer.id}` : `/offertes/${customer.id}`)
            }
            className="shrink-0 text-xs text-emerald-700 hover:text-emerald-900 underline whitespace-nowrap"
          >
            Open klant
          </button>
        )}
      </div>

      {openOffertes.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Open offertes</div>
          <ul className="space-y-1">
            {openOffertes.slice(0, compact ? 2 : 3).map((o) => (
              <li key={o.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/offertes/${o.id}`)}
                  className="w-full flex items-center justify-between text-xs text-left hover:bg-gray-50 rounded px-1 py-1"
                >
                  <span className="text-gray-700 truncate">
                    {o.status}
                    {o.totalInclBtw !== null ? ` · ${formatEuro(o.totalInclBtw)}` : ''}
                  </span>
                  <span className="text-gray-400">{o.createdDate ? relativeTime(o.createdDate) : ''}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {recentCalls.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Recent contact</div>
          <ul className="space-y-1">
            {recentCalls.slice(0, 3).map((c) => (
              <li key={c.id} className="flex items-center justify-between text-xs">
                <span className="text-gray-700 inline-flex items-center gap-1">
                  {c.direction === 'inbound' ? (
                    <PhoneInboundIcon className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                  ) : (
                    <PhoneOutboundIcon className="w-3.5 h-3.5 shrink-0 text-gray-400" />
                  )}
                  {c.direction === 'inbound' ? 'Inkomend' : 'Uitgaand'}
                  {c.status ? ` · ${c.status}` : ''}
                </span>
                <span className="text-gray-400">{relativeTime(c.started_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {accountId && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Lopende orders</div>
          {ordersLoading ? (
            <div className="space-y-1">
              <div className="h-4 bg-gray-100 rounded animate-pulse" />
              <div className="h-4 bg-gray-100 rounded animate-pulse w-3/4" />
            </div>
          ) : orders.length === 0 ? (
            <p className="text-xs text-gray-400">Geen lopende orders</p>
          ) : (
            <ul className="space-y-1">
              {orders.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/orders/${o.id}`)}
                    className="w-full flex items-center justify-between text-xs text-left hover:bg-gray-50 rounded px-1 py-1"
                  >
                    <span className="text-gray-700 font-mono truncate">{o.orderNumber}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusBadgeClass(o.status)}`}>
                      {o.status}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {!compact && (
        <div>
          <button
            type="button"
            onClick={() => setShowMore((v) => !v)}
            className="text-xs text-emerald-700 hover:underline"
          >
            {showMore ? 'Minder info' : 'Meer info'}
          </button>
          {showMore && (
            <div className="mt-2 space-y-1 text-xs text-gray-500">
              <div>Openstaand: <span className="text-gray-400">—</span></div>
              <div>Klachten: <span className="text-gray-400">—</span></div>
              <div>Reviews: <span className="text-gray-400">—</span></div>
              <div className="text-[10px] text-gray-400 pt-1">
                Deze cijfers worden ingevuld na de Brein-migratie.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
